/**
 * <pdp-gallery> — the PDP v2 media gallery.
 *
 * Replaces Dawn's <media-gallery> on the redesign template. Navigation is native scrolling on a
 * scroll-snap track: the element sets scrollLeft, the browser animates, and a scroll listener
 * reports which slide is active. No layout maths, so the tablet peek is pure CSS.
 *
 * IMPORTANT: exposes setActiveMedia(mediaId) with the same call signature Dawn's product-info.js
 * uses on its own media-gallery, so variant-driven image switching keeps working.
 */
class PdpGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.pdp-v2__track');
    this.slides = Array.from(this.querySelectorAll('.pdp-v2__slide'));
    this.thumbs = Array.from(this.querySelectorAll('.pdp-v2__thumb'));
    this.pips = Array.from(this.querySelectorAll('.pdp-v2__pip'));
    // Every badge inside <product-info>, not just the gallery's own. Tablet renders a second one in
    // the title block (inventory §3), and a badge this element cannot see is a badge that goes
    // stale over a full-price variant — the false pricing claim that was a BLOCKING finding.
    this.badges = Array.from(
      (this.closest('product-info') ?? this).querySelectorAll('.pdp-v2__sale-badge')
    );
    this.status = this.querySelector('[id^="GalleryStatus"]');

    // Subscribed BEFORE the slides guard below. A gallery that renders with zero slides would
    // otherwise return early and never wire this up, leaving every badge frozen at its load-time
    // sale state — a false pricing claim over a full-price variant, which was a BLOCKING finding.
    // snippets/pdp-product-name.liquid gates its badge on the product having media, so the case
    // where <pdp-gallery> is not rendered at all cannot strand a badge either.
    this._unsubBadges = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      if (data?.sectionId && data.sectionId !== this.dataset.section) return;
      if (!this.badges.length || !data?.variant) return;
      const { price, compare_at_price } = data.variant;
      const onSale = typeof compare_at_price === 'number' && compare_at_price > price;
      this.badges.forEach((b) => { b.hidden = !onSale; });
    });

    if (!this.track || this.slides.length === 0) return;

    this.index = Math.max(0, this.slides.findIndex((s) => s.dataset.active === 'true'));

    this.querySelector('.pdp-v2__arrow--prev')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('.pdp-v2__arrow--next')?.addEventListener('click', () => this.go(this.index + 1));

    this.thumbs.forEach((t) =>
      t.addEventListener('click', () => this.setActiveMedia(t.dataset.mediaId))
    );

    // Native scroll is the source of truth for which slide is showing — swipe, arrow key and
    // programmatic scroll all converge here rather than each maintaining their own idea of state.
    this.track.addEventListener('scroll', () => {
      clearTimeout(this._t);
      this._t = setTimeout(() => this.syncFromScroll(), 60);
    });

    this.track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1); }
    });

    // Clicking a slide's image opens Dawn's product-modal. The <li>.pdp-v2__slide already carries
    // data-media-id (see pdp-gallery.liquid), so it doubles as the opener: product-modal.js's
    // show(opener) reads opener.getAttribute('data-media-id') to pick which media to display, and
    // calls opener.focus() on hide — both work on the real <li> element, no synthetic object needed.
    this.slides.forEach((s) => {
      s.querySelector('img')?.addEventListener('click', () => this.openLightbox(s));
    });

    // Dawn's product-info.js diffs `media-gallery ul` on variant change; our element is not a
    // media-gallery, so that path cannot reach us. Subscribe to the published event instead —
    // an explicit contract rather than a DOM shape Dawn happens to look for.
    //
    // VERIFIED against this theme: pubsub.js and constants.js are CLASSIC scripts loaded from
    // layout/theme.liquid:31-32. `function subscribe()` becomes a global, but
    // `const PUB_SUB_EVENTS` does NOT become window.PUB_SUB_EVENTS — const never creates a window
    // property. Reference both as BARE IDENTIFIERS exactly as Dawn's own code does
    // (product-info.js:23,204). A `window.PUB_SUB_EVENTS?.` guard would be permanently undefined
    // and the subscription would silently never happen.
    this._unsub = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      if (data?.sectionId && data.sectionId !== this.dataset.section) return;
      const id = data?.variant?.featured_media?.id;
      if (id) this.setActiveMedia(id);

      // Finding 1 (PRODUCT-INFO-CONTRACT final review): on_sale is otherwise computed once at
      // render and never revisited, so the badge goes stale the moment a shopper switches to a
      // variant with different sale state. data.variant is the raw Shopify variant JSON payload
      // product-info.js publishes (product-info.js:204-210) — price/compare_at_price are integers
      // in the store's minor currency unit, so a plain numeric comparison is correct without any
      // formatting. Toggle visibility only; never rebuild the badge's markup.
    });

    this.sync();
  }

  disconnectedCallback() {
    this._unsub?.();
    this._unsubBadges?.();
    this.stopSlide();
  }

  /**
   * Our lightbox (see the markup note in pdp-gallery.liquid). Fitted on open; a click toggles zoom
   * to the 2400px rendition around the clicked point; scroll pans; Esc / close / backdrop close.
   */
  openLightbox(opener) {
    // The dialog sits AFTER </pdp-gallery> in the snippet (a sibling, not a descendant), so it is
    // found by the section-scoped id rather than from this element.
    const box = document.getElementById(`PdpLightbox-${this.dataset.section}`);
    if (!box) return;
    const img = box.querySelector('.pdp-v2__lightbox-img');
    const scroll = box.querySelector('.pdp-v2__lightbox-scroll');
    const src = opener.dataset.full;
    const slideImg = opener.querySelector('img');
    if (img.getAttribute('src') !== src) {
      img.src = src;
      img.alt = slideImg?.alt || '';
    }
    box.classList.remove('is-zoomed');
    this._lightboxOpener = opener;
    this._lightboxIndex = Math.max(0, this.slides.indexOf(opener));
    const prev = box.querySelector('.pdp-v2__lightbox-prev');
    const next = box.querySelector('.pdp-v2__lightbox-next');
    box.classList.toggle('is-single', this.slides.length < 2);
    // Previous / next through the same images as the slider (user, 2026-10-08), the slider following
    // behind so closing lands on the image that was showing.
    const show = (i) => {
      const n = Math.max(0, Math.min(i, this.slides.length - 1));
      const slide = this.slides[n];
      if (!slide) return;
      this._lightboxIndex = n;
      this._lightboxOpener = slide;
      img.src = slide.dataset.full;
      img.alt = slide.querySelector('img')?.alt || '';
      box.classList.remove('is-zoomed');
      scroll.scrollLeft = 0;
      scroll.scrollTop = 0;
      if (prev) prev.disabled = n === 0;
      if (next) next.disabled = n === this.slides.length - 1;
      this.go(n);
    };
    this._lightboxShow = show;
    if (prev) prev.disabled = this._lightboxIndex === 0;
    if (next) next.disabled = this._lightboxIndex === this.slides.length - 1;

    if (!box._wired) {
      box._wired = true;
      box.querySelector('[data-lightbox-close]').addEventListener('click', () => box.close());
      box.addEventListener('click', (e) => { if (e.target === box || e.target === scroll) box.close(); });
      img.addEventListener('click', (e) => {
        const zoomIn = !box.classList.contains('is-zoomed');
        // Where on the image was clicked, as a fraction, BEFORE the size changes.
        const r = img.getBoundingClientRect();
        const fx = (e.clientX - r.left) / r.width;
        const fy = (e.clientY - r.top) / r.height;
        box.classList.toggle('is-zoomed', zoomIn);
        if (zoomIn) {
          // Land the clicked point under the pointer: scroll so that fraction of the zoomed image
          // sits where the pointer is in the viewport.
          const zr = img.getBoundingClientRect();
          scroll.scrollLeft = Math.max(0, fx * zr.width - e.clientX);
          scroll.scrollTop = Math.max(0, fy * zr.height - e.clientY);
        } else {
          scroll.scrollLeft = 0;
          scroll.scrollTop = 0;
        }
      });
      box.querySelectorAll('[data-lightbox-step]').forEach((b) => b.addEventListener('click', (e) => {
        e.stopPropagation();
        this._lightboxShow(this._lightboxIndex + Number(b.dataset.lightboxStep));
      }));
      box.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this._lightboxShow(this._lightboxIndex + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this._lightboxShow(this._lightboxIndex - 1); }
      });
      // A horizontal swipe on the fitted image steps too; zoomed, a drag pans instead.
      let sx = null;
      let sy = null;
      scroll.addEventListener('pointerdown', (e) => { if (!box.classList.contains('is-zoomed')) { sx = e.clientX; sy = e.clientY; } });
      scroll.addEventListener('pointerup', (e) => {
        if (sx === null) return;
        const dx = e.clientX - sx;
        const dy = e.clientY - sy;
        sx = null;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          box._swiped = true;   // swallow the click that follows, so it doesn't zoom or close
          this._lightboxShow(this._lightboxIndex + (dx < 0 ? 1 : -1));
        }
      });
      box.addEventListener('click', (e) => { if (box._swiped) { box._swiped = false; e.stopImmediatePropagation(); e.preventDefault(); } }, true);
      box.addEventListener('close', () => {
        document.documentElement.classList.remove('pdp-v2-scroll-lock');
        this._lightboxOpener?.focus?.({ preventScroll: true });
        // The dialog hands focus back to what opened it (the FIRST image clicked), and that focus
        // scrolls the track back to it. Land the slider on the image the lightbox was showing.
        const i = this._lightboxIndex ?? this.index;
        requestAnimationFrame(() => {
          const slide = this.slides[i];
          if (!slide) return;
          this.stopSlide();
          this.track.scrollLeft = slide.offsetLeft - this.track.offsetLeft;
          this.index = i;
          this.sync();
        });
      });
    }
    box.showModal();
    scroll.scrollLeft = 0;
    scroll.scrollTop = 0;
    document.documentElement.classList.add('pdp-v2-scroll-lock');
  }

  go(i) {
    const next = Math.max(0, Math.min(i, this.slides.length - 1));
    const slide = this.slides[next];
    if (!slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const left = slide.offsetLeft - this.track.offsetLeft;
    if (reduce) {
      this.stopSlide();
      this.track.scrollTo({ left, behavior: 'auto' });
    } else {
      this.slideTo(left);
    }
    this.index = next;
    this.sync();
  }

  /*
   * The slide between images. History: the prototype's 600ms ease-out (designer, Pastel #32, over
   * native smooth scrolling that ran 198ms in Safari) was first done by writing scrollLeft every
   * frame -- the user then found it "heavy and a bit choppy" (2026-10-08). Writing scrollLeft per frame
   * runs on Safari's main thread; now the track JUMPS to the target and the slides are put back where
   * they were with a transform and glide home (FLIP), which the compositor runs. 500ms on an
   * ease-out-cubic, lighter in the tail than CSS's ease-out. A wheel, touch or drag cancels it.
   */
  slideTo(left) {
    this.stopSlide();
    const track = this.track;
    const dist = left - track.scrollLeft;
    if (Math.abs(dist) < 1) return;
    if (!this._stopBound) {
      this._stopBound = () => this.stopSlide();
      track.addEventListener('wheel', this._stopBound, { passive: true });
      track.addEventListener('pointerdown', this._stopBound, { passive: true });
      track.addEventListener('touchstart', this._stopBound, { passive: true });
    }
    // Snapping stays OFF for the glide: snap positions follow the slides' TRANSFORMED boxes, so with
    // snapping on the browser re-snaps the track back toward the old slide while they are offset.
    track.style.scrollSnapType = 'none';
    track.scrollLeft = left;
    this.slides.forEach((s) => { s.style.transition = 'none'; s.style.transform = `translateX(${dist}px)`; });
    void track.offsetWidth;   // commit the offset before the transition starts
    this.slides.forEach((s) => {
      s.style.transition = 'transform 500ms cubic-bezier(0.33, 1, 0.68, 1)';
      s.style.transform = 'translateX(0)';
    });
    this._slideTimer = setTimeout(() => this.stopSlide(), 520);
  }

  stopSlide() {
    clearTimeout(this._slideTimer);
    this._slideTimer = 0;
    (this.slides || []).forEach((s) => { s.style.transition = ''; s.style.transform = ''; });
    if (this.track) this.track.style.scrollSnapType = '';
  }

  syncFromScroll() {
    const mid = this.track.scrollLeft + this.track.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    this.slides.forEach((s, i) => {
      const c = s.offsetLeft - this.track.offsetLeft + s.clientWidth / 2;
      const d = Math.abs(c - mid);
      if (d < bestDist) { bestDist = d; best = i; }
    });
    if (best !== this.index) { this.index = best; this.sync(); }
  }

  sync() {
    this.slides.forEach((s, i) => s.toggleAttribute('data-active', i === this.index));
    this.thumbs.forEach((t, i) => {
      if (i === this.index) t.setAttribute('aria-current', 'true');
      else t.removeAttribute('aria-current');
    });
    this.pips.forEach((p, i) => p.toggleAttribute('data-active', i === this.index));

    const prev = this.querySelector('.pdp-v2__arrow--prev');
    const next = this.querySelector('.pdp-v2__arrow--next');
    if (prev) prev.disabled = this.index === 0;
    if (next) next.disabled = this.index === this.slides.length - 1;

    // Finding 2 (PRODUCT-INFO-CONTRACT final review): Dawn's media-gallery announces slide changes
    // through a role="status" live region (snippets/product-media-gallery.liquid:47,54); ours had
    // the visual affordances (aria-current, aria-label) but no announcement. window.accessibilityStrings
    // is a theme-wide global set in layout/theme.liquid:357-358 (read, never edited) — reusing it
    // keeps the announcement text identical to Dawn's own gallery.
    if (this.status) {
      const template = window.accessibilityStrings?.imageAvailable || 'Image [index] is now available in gallery view';
      this.status.textContent = template.replace('[index]', this.index + 1);
    }
  }

  /** Called by Dawn's product-info.js on variant change. Same signature as media-gallery's. */
  setActiveMedia(mediaId) {
    const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
    if (i >= 0) this.go(i);
  }
}

if (!customElements.get('pdp-gallery')) customElements.define('pdp-gallery', PdpGallery);
